(ns frontend.view.grid.companies-grid.events
  (:require
   [clojure.string :as string]
   [common.intelligence.common :as intelligence]
   [common.localization :as loc]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.conn :as conn]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [common.entity :as entity]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.view.grid.companies-grid.columns :as columns]
   [frontend.view.grid.companies-grid.db :as db]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.error-message.view :as error-message]
   [frontend.view.modals.events :as modals]
   [frontend.utils :as fu]
   [promesa.core :as p]
   [reagent.core :as r]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :companies-view-settings]
   rest))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/company)
           (remove (fn [[k v]]
                     (let [grid-visibility (->> v
                                                :field/default-visibility
                                                :grid)]
                       (or (nil? grid-visibility)
                           (= grid-visibility :editor.type/none)
                           (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                                   (not (:field/secondary-field? v)))
                             (not (f-permissions/field-read-access? k :entity.type/company nil))
                             (not (f-permissions/field-read-access? (:field/related-fields v)
                                                                    :entity.type/company
                                                                    nil)))))))
           (sort-by (fn [[k v]]
                      (-> v
                          :field/translations
                          :en)))))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn companies-column-keys
  []
  (concat
   (when (session/is-admin?) [:db/id])
   (map first (keys-and-schemas))))

(def default-kanban-attributes [:asset/description :company/website])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set
         (concat @db/selected-columns
                 attributes
                 default-kanban-attributes
                 [footer-1 footer-2 (entity-types/type->owner-attribute :entity.type/company)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/companies
   :entity-type :entity.type/company
   :modal-state db/modal-state
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :selected-pipeline nil
   :ls-path ls-path
   :kanban? true
   :kanban-args {:default-grouped-by  :company/company-owner
                 :kanban-state        db/kanban
                 :grouped-by-state    db/grouped-by
                 :card-settings-state db/card-settings
                 ;;for long card view:
                 :default-attributes  default-kanban-attributes
                 :expanded-state      db/expanded
                 :sorted-kanban-entities-state db/sorted-kanban-entities
                 :lists-state         db/lists
                 :expanded?           expanded?
                 :toggle-expand!      toggle-expand!}
   :column-keys companies-column-keys
   :default-columns columns/default-columns
   :mandatory? columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or @db/card-mode
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/companies)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/companies)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/companies)]
    (r/dispose! tracker))
  (swap! db/companies dissoc
    :loaded
    :schema
    :entities
    :entity-types
    :view-settings
    :sorted-grid-entities
    :search
    :kanban
    :sorted-kanban-entities
    :selected
    :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/companies update :show-quick-filters? not))

(defn system-views
  []
  [{:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :asset/name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode}
    :named-view/name (loc/tr :all-companies)
    :disable-delete? true
    :db/id "named-view-all-companies"}
   {:named-view/settings {:selected-columns columns/my-companies-columns
                          :ordered-columns  columns/my-companies-ordered-columns
                          :sort-by          :asset/name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode
                          :filters          {:company/company-owner {:operand :operand/any-of
                                                                     :lower-bound-value
                                                                     #{@session/account-id}}}}
    :named-view/name (loc/tr :my-companies)
    :disable-delete? true
    :db/id "named-view-my-companies"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :asset/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode}
    :named-view/name (loc/tr :company-pipeline)
    :disable-delete? true
    :db/id "named-view-company-pipeline"}])

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :companies-grid)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn company-updates
  []
  (let [enriched-companies (->> @session/all-companies
                                (mapv intelligence/enrich-entity))]
    (swap! db/companies assoc :grid-entities enriched-companies)))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/companies assoc :loaded true)
                    (deref db/companies))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/companies assoc
      :tracker-1 (r/track! company-updates)
      :tracker-2 (r/track! permission-updates)
      :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :companies-grid
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn mass-delete!
  []
  (-> (conn/sendp! :company/mass-action
                   {:action      :delete-companies
                    :company-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_args]
                (swap! db/companies assoc :selected {})))
      (p/catch (fn [{:keys [error message] :as e}]
                 (let [show-error-message? (and (= error :bad-request)
                                                (string? message)
                                                (string/includes? message "map-profile"))]
                   (modals/open-modal!
                    error-message/modal-key
                    {:title          (loc/tr :attention)
                     :body-content   [:span (loc/tr :failed-to-delete-entity)
                                      (when show-error-message?
                                        [:<>
                                         [:br] [:br]
                                         [:span "(" message ")"]])]
                     :ok-button-text (loc/tr :ok)}))
                 (logging/error "Unable to delete selected companies" e)))))

(defn delete-selected-clicked
  [_]
  (let [company-ids (grid/get-selected-items (setup-args))
        target-in (map session/target-asset-in company-ids)
        cust-in (map session/customer-in company-ids)
        member-in (map session/kpi-member-in company-ids)
        fund-gp-in (map session/gp-in-funds company-ids)]
    (cond
      (not-every? empty? target-in)
      (modals/open-modal!
       error-message/modal-key
       {:body-content   (loc/tr :target-asset-cannot-be-deleted)
        :title          (loc/tr :attention)
        :ok-button-text (loc/tr :back)})

      (not-every? empty? member-in)
      (modals/open-modal!
       error-message/modal-key
       {:body-content   (loc/tr :target-asset-is-kpi-member)
        :title          (loc/tr :attention)
        :ok-button-text (loc/tr :back)})

      (not-every? empty? fund-gp-in)
      (let [fund-gp-companies
            (->> fund-gp-in
                 (map-indexed (fn [index participant-entities]
                                (when (seq participant-entities)
                                  (let [person-id (nth company-ids index)
                                        person (session/get-entity person-id)]
                                    person))))
                 (remove nil?))
            company-names (->> fund-gp-companies
                               (map entity/display-string)
                               (cu/join-with-and))
            body-text (if (= (count fund-gp-companies) 1)
                        (loc/tr :fund-gp-company-cannot-be-deleted [company-names])
                        (loc/tr :fund-gp-companies-cannot-be-deleted [company-names]))]
        (modals/open-modal!
         error-message/modal-key
         {:body-content   body-text
          :title          (loc/tr :attention)
          :ok-button-text (loc/tr :back)}))

      (not-every? empty? cust-in)
      (modals/open-modal! error-message/modal-key
                          {:body-content   (loc/tr :customer-cannot-be-deleted)
                           :title          (loc/tr :attention)
                           :ok-button-text (loc/tr :back)})

      :else
      (modals/open-modal!
       confirmation/modal-key
       {:body          [:div
                        [:p (loc/tr :confirm-delete-selected)]]
        :confirm-text  (loc/tr :confirm-delete-selected-button)
        :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
        :on-confirm    #(mass-delete!)}))))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))
